import { SHADOW_DEX_IDS_GENERATED } from "./generated/shadowRoster.generated";
import { __MEGA_SLUG_BY_ID, __GMAX_SLUG_BY_ID } from "./megaGmaxSprites";

/**
 * Pokémon GO availability rosters (Push 4 / v1.4.3 b4).
 *
 * Background
 * ----------
 * Before this module the Dex grid had exactly two roster policies
 * (see the old dexGridFilter.baseDexNumbers):
 *   - non-sparse variants  → the full National Dex 1..1025
 *   - sparse variants      → ONLY entries the user had already recorded
 *
 * That made several tabs wrong:
 *   - Mega / G-Max showed *nothing* until a scan recorded an entry, so a
 *     fresh install had "0 shown" on those tabs even though Pokémon GO has
 *     a fixed, knowable roster of Mega- and Gigantamax-capable species.
 *   - The "Hisui" region filter matched zero ids (the REGIONS table had an
 *     inverted range [906,905] and regionForId() explicitly skipped it),
 *     so picking Hisui always produced "0 shown" and stranded the Hisui
 *     evolution line in the catch-all "Unknown" bucket.
 *
 * This module is the single source of truth for "what species can appear
 * on each Dex tab / region in Pokémon GO right now". It is intentionally a
 * curated, documented list rather than a derived guess — the user direction
 * was explicit: do not silently fabricate rosters.
 *
 * Numbering
 * ---------
 * All ids are **canonical National Dex numbers** matching
 * client/src/data/speciesNames.ts (e.g. #899 Wyrdeer, #903 Sneasler).
 * The task brief referenced "#0900 Wyrdeer" using a +1 offset scheme; we
 * deliberately follow the repo's authoritative numbering instead so the
 * silhouette atlas, names and roster all line up.
 *
 * Sources / rationale are documented inline next to each roster and proven
 * by pogoAvailability.test.ts (G-Max > 0, Mega > 0, Shadow > 0,
 * Purified == Shadow, Hisui = the exact researched set, etc.).
 *
 * Weekly maintenance
 * ------------------
 * The Shadow / Mega / G-Max rosters rotate as Niantic releases new species.
 * To keep the Dex tabs current, edit ONLY the `*_DEX_IDS` arrays below and
 * bump `ROSTER_LAST_REVIEWED`. Everything downstream (the curated-roster
 * lookup, the Dex grid silhouettes, Purified mirroring Shadow) is derived
 * from these arrays, so a single edit per category is all a weekly refresh
 * needs. See ROSTER_SOURCES for where to check the live availability.
 */

import type { PokedexVariant } from "./pogoPokedexVariantClassifier";
import { formDexSpeciesIds } from "./formDexRoster";

/**
 * Last date the category rosters below were reviewed against live Pokémon GO
 * availability. Update this whenever you patch any `*_DEX_IDS` array so the
 * UI / future maintainers know how fresh the curated lists are.
 */
export const ROSTER_LAST_REVIEWED = "2026-10-25" as const;

/**
 * Where to verify current availability during a weekly refresh. These are
 * documentation pointers only (not fetched at runtime) — the rosters are
 * curated by hand on purpose so we never silently fabricate entries.
 */
export const ROSTER_SOURCES = {
  shadow:
    "Bulbapedia, List of Shadow Pokémon in Pokémon GO " +
    "(https://bulbapedia.bulbagarden.net/wiki/List_of_Shadow_Pok%C3%A9mon_in_Pok%C3%A9mon_GO); " +
    "cross-checked against Team GO Rocket lineups / LeekDuck Shadow trackers",
  mega:
    "PoGoAPI mega feed (https://pogoapi.net/api/v1/mega_pokemon.json) as the " +
    "automated core, plus the auditable overlay in megaRosterOverrides.ts for " +
    "announced-but-missing entries. Audit drift with `npm run generate:mega`.",
  gmax:
    "Bulbapedia, Gigantamax (GO) release-date table " +
    "(https://bulbapedia.bulbagarden.net/wiki/Gigantamax_(GO)). Membership is " +
    "every Gigantamax form (GMAX_DEX_IDS); the released subset is GMAX_RELEASED_IDS.",
  dynamax:
    "Bulbapedia, Dynamax (GO) released-by-date table " +
    "(https://bulbapedia.bulbagarden.net/wiki/Dynamax_(GO)); cross-checked " +
    "against LeekDuck Max Battle / Max Monday events. No clean feed exists " +
    "for the RELEASED roster (the Game Master's breadTierGroup is latent " +
    "tiering for the whole dex, not the release list), so this stays curated. " +
    "Audit drift with `npm run generate:dynamax`.",
} as const;

/**
 * Hisui regional-evolution line as it appears in the Pokémon GO "Hisui"
 * Pokédex grouping. These are the seven Gen-8 Hisui *evolutions*
 * (#899-#905), NOT the Hisuian regional *forms* (Hisuian Growlithe, etc.)
 * which live under their original Kanto/Johto/Unova dex numbers.
 *
 * Why the evolutions and not the forms: the in-game Hisui grid lists the
 * dedicated Hisui dex entries (Wyrdeer … Enamorus). Regional forms are
 * surfaced under their base species' region, exactly like Alolan/Galarian
 * forms are. Keeping Hisui = the evolution line also avoids double-listing
 * a species in two regions.
 *
 *   899 Wyrdeer · 900 Kleavor · 901 Ursaluna · 902 Basculegion ·
 *   903 Sneasler · 904 Overqwil · 905 Enamorus
 *
 * Pokémon GO availability note: not every member of this grouping is
 * catchable in-game at a given time. We still list all 7 so the tab shows the
 * complete Hisui grouping as silhouettes — "available in the Dex grouping" is
 * the product meaning here, and the user's caught/missing state is overlaid on
 * top. Release state comes from the roster, never from this list.
 */
export const HISUI_DEX_IDS: readonly number[] = [
  899, 900, 901, 902, 903, 904, 905,
] as const;

/**
 * Hisuian regional FORMS and their Pokémon-GO typings, sourced from
 * Bulbapedia's regional-forms reference. Kept separate from
 * HISUI_DEX_IDS on purpose (these are alternate forms of existing dex
 * numbers, surfaced under their base region). Exposed so the type-filter
 * and tests can reason about Hisuian typings without polluting the base
 * National Dex type table in types.ts.
 *
 * id → [primary, secondary?]
 */
export const HISUIAN_FORM_TYPES: Record<number, [string, string?]> = {
  58: ["fire", "rock"],      // Hisuian Growlithe
  59: ["fire", "rock"],      // Hisuian Arcanine
  100: ["electric", "grass"],// Hisuian Voltorb
  101: ["electric", "grass"],// Hisuian Electrode
  157: ["fire", "ghost"],    // Hisuian Typhlosion
  211: ["dark", "poison"],   // Hisuian Qwilfish
  215: ["fighting", "poison"],// Hisuian Sneasel
  503: ["water", "dark"],    // Hisuian Samurott
  549: ["grass", "fighting"],// Hisuian Lilligant
  570: ["normal", "ghost"],  // Hisuian Zorua
  571: ["normal", "ghost"],  // Hisuian Zoroark
  628: ["psychic", "flying"],// Hisuian Braviary
  705: ["steel", "dragon"],  // Hisuian Sliggoo
  706: ["steel", "dragon"],  // Hisuian Goodra
  713: ["ice", "rock"],      // Hisuian Avalugg
  724: ["grass", "fighting"],// Hisuian Decidueye
  903: ["fighting", "poison"],// Sneasler (Hisui evo)
  904: ["dark", "poison"],   // Overqwil  (Hisui evo)
};

/**
 * Released Mega-Evolution-capable species in Pokémon GO (National Dex ids).
 *
 * Data-driven, two layers (see megaGmaxSprites.ts where the merge happens):
 *   1. CORE — kept in step with PoGoAPI's mega feed
 *      (https://pogoapi.net/api/v1/mega_pokemon.json), the canonical automated
 *      signal. The weekly audit `npm run generate:mega` flags any drift.
 *   2. OVERLAY — `megaRosterOverrides.ts`, a small auditable patch list for
 *      announced-but-not-yet-in-PoGoAPI Megas (the Super Max / Super Mega Raid
 *      additions such as Falinks, Dragonite, Malamar, Victreebel). PoGoAPI lags
 *      the live game by weeks, so without this the Mega dex would drop species
 *      players can already obtain. Each overlay entry carries source URLs and
 *      review/expiry metadata.
 *
 * Both layers feed the merged MEGA_SLUG_BY_ID table; this roster is just its
 * sorted keys. The tests enforce it is non-empty, a curated subset (not the
 * full 1025), and contains both well-known anchors AND the overlay candidates.
 *
 * Super Mega / Super Max are NOT a separate dex: GO exposes no distinct
 * Pokédex page for them (Super Max is a fourth Mega *Level*; Super Mega Raids
 * are a *raid type*), so these species live here in the Mega dex with their
 * provenance recorded as a `megaLevel` tag on the override, not as a new dex
 * variant. See megaRosterOverrides.ts for the full rationale.
 *
 * Note: ids are the *base* species (e.g. 3 Venusaur covers Mega Venusaur),
 * because the silhouette atlas and dexStateStore are keyed by base species.
 * Charizard (6) and Mewtwo (150) each have two Mega forms in lore but a
 * single base-species dex entry, so they appear once.
 */
export const MEGA_DEX_IDS: readonly number[] = Object.keys(__MEGA_SLUG_BY_ID)
  .map(Number)
  .sort((a, b) => a - b);

/**
 * Released Gigantamax / G-Max species in Pokémon GO (National Dex ids).
 *
 * Gigantamax arrived with the Max Out / Dynamax update. This is a curated
 * snapshot of the species that have had a released Gigantamax form in
 * Pokémon GO around this build: the Galar starters' final stages and the
 * early event G-Max releases (Venusaur/Charizard/Blastoise/Gengar/etc.).
 *
 * The user direction is explicit that this tab must NOT be empty. Tests
 * enforce length > 0 and presence of the Galar starters. When more G-Max
 * release in-game, append ids here. Ids are base species (Gigantamax forms
 * share the base dex number).
 */
export const GMAX_DEX_IDS: readonly number[] = Object.keys(__GMAX_SLUG_BY_ID)
  .map(Number)
  .sort((a, b) => a - b);

/**
 * Gigantamax forms Pokémon GO has actually RELEASED (National Dex ids): the
 * release axis of the G-Max dex.
 *
 * GMAX_DEX_IDS above is the dex's MEMBERSHIP: every Gigantamax form the
 * silhouette atlas knows, shipped in GO or not, so the G-Max dex shows what is
 * still to come the way the Main dex shows an unreleased species (owner rule
 * 2026-10-09: no date locks on upcoming cells). This list is the subset GO has
 * shipped. A member outside it draws the red "unreleased" callout, drops out
 * of the Released count and the In-GO ring, and a scan can never write it (a
 * registered Gigantamax that does not exist yet is a scanner mis-read).
 *
 * Source: Bulbapedia, Gigantamax (GO), "List of Gigantamax Pokémon" with
 * release dates (https://bulbapedia.bulbagarden.net/wiki/Gigantamax_(GO)),
 * cross-checked against Serebii's Max Battles list and the LeekDuck Max Battle
 * Day events on 2026-10-25. When a new Gigantamax is announced, append its base
 * id with the debut date (from the announcement, not the debut) and bump
 * ROSTER_LAST_REVIEWED. Every id must be a GMAX_DEX_IDS member (test-pinned).
 */
export const GMAX_RELEASED_IDS: readonly number[] = [
  3, // Venusaur, 2024-10-26
  6, // Charizard, 2024-10-26
  9, // Blastoise, 2024-10-26
  12, // Butterfree, 2025-08-03
  25, // Pikachu, 2026-03-28
  52, // Meowth, 2026-02-15
  68, // Machamp, 2025-05-25
  94, // Gengar, 2024-10-31
  99, // Kingler, 2025-02-01
  131, // Lapras, 2024-12-08
  143, // Snorlax, 2025-04-19
  569, // Garbodor, 2025-11-01
  812, // Rillaboom, 2025-05-29
  815, // Cinderace, 2025-06-05
  818, // Inteleon, 2025-06-12
  849, // Toxtricity, 2024-11-16
  861, // Grimmsnarl, 2025-11-07
  884, // Duraludon, 2026-11-06 (announced for GO Wild Area 2026, listed from the announcement)
  // Still to come as of 2026-10-25: Eevee #133, Melmetal #809, Corviknight
  // #823, Orbeetle #826, Drednaw #834, Coalossal #839, Flapple #841, Appletun
  // #842, Sandaconda #844, Centiskorch #851, Alcremie #869, Copperajah #879
  // and Urshifu #892 (both styles). Hatterene #858 has a pre-provisioned atlas
  // tile but no roster cell until GO announces it.
];
const GMAX_RELEASED_SET: ReadonlySet<number> = new Set(GMAX_RELEASED_IDS);

/**
 * Released Dynamax species in Pokémon GO (National Dex ids).
 *
 * Dynamax is the broad Max-Battle roster; Gigantamax (GMAX_DEX_IDS) is a
 * special-form subset of it. Unlike G-Max/Shadow, Dynamax is NOT a scannable
 * registered Pokédex screen in Pokémon GO, so this dex is manual-only (see
 * MANUAL_ONLY_VARIANTS) — but it still has a fixed, knowable roster, so it
 * renders curated silhouettes like the sparse tabs do.
 *
 * WHY CURATED, NOT GENERATED
 * --------------------------
 * There is no clean feed for the RELEASED Dynamax roster. The PokéMiners Game
 * Master carries a `breadTierGroup` field ("bread" = their Max-Battles
 * codename) but stamps it on ~2,450 templates — the whole dex's latent
 * difficulty tiering, NOT the release list. ScrapedDuck's `max-battles` events
 * are generic-only (the species is in the title, e.g. "Dynamax Deino during
 * Max Monday" — the same unparseable case as `raid-day`). So the released set
 * is community-curated by release date (Bulbapedia / LeekDuck).
 *
 * This list is seeded from the Bulbapedia "Dynamax (GO)" released-by-date
 * table and grows almost weekly at Max Monday events — append base ids here
 * and bump ROSTER_LAST_REVIEWED. `npm run generate:dynamax` audits this list
 * against the Game Master's `breadTierGroup` (every id must be Max-tier
 * eligible) and surfaces Max-Battle event sightings not yet listed here. Ids
 * are base species (Dynamax does not change a species' dex number).
 */
export const DYNAMAX_DEX_IDS: readonly number[] = [
  1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 25, 26, 58, 59, 63, 64, 65, 66, 67,
  68, 92, 93, 94, 98, 99, 106, 107, 111, 112, 113, 125, 126, 129, 130, 133, 134, 135,
  136, 138, 139, 140, 141, 144, 145, 146, 163, 164, 196, 197, 213, 215, 237, 242,
  243, 244, 245, 249, 250, 280, 281, 282, 302, 320, 321, 328, 329, 330, 349,
  350, 363, 364, 365, 374, 375, 376, 377, 378, 379, 380, 381, 415, 416, 461, 464, 466,
  467, 470, 471, 475, 480, 481, 482, 483, 484, 519, 520, 521, 524, 525, 526, 527, 528, 529, 530, 546,
  547, 554, 555, 568, 569, 615, 633, 634, 635, 677, 678, 686, 687, 700, 761, 762, 763, 766,
  780, 808, 810, 811, 812, 813, 814, 815, 816, 817, 818, 819, 820, 821, 822, 823,
  831, 832, 849, 856, 857, 858, 870, 884, 891, 892, 895,
  // 2026-09-14: Dynamax Rhyhorn (Max Monday 2026-09-14) and its line. Dynamax
  // survives evolution, so Rhydon and Rhyperior ride in with it, the same way
  // every other line above is listed whole.
  // 2026-10-20 (LeekDuck, announced debuts): Uxie, Mesprit and Azelf (#480 to
  // #482, Max Battle Day 2026-10-24, one per region), Meltan (#808, GO Pass:
  // Into the Wild from 2026-11-09), Regidrago (#895, Max Battle Hour
  // 2026-11-12), Dialga and Palkia (#483, #484, GO Wild Area 2026-11-14 and
  // 15). Listed from the announcement, not the debut (owner rule 2026-10-09:
  // no date locks on upcoming cells, they must show in upcoming comparisons).
  // Same pass, from the audit's Max Monday titles: Sneasel (#215, 2026-10-19)
  // with Weavile (#461), and Espurr (#677, 2026-11-23) with Meowstic (#678),
  // lines listed whole as above.
];

/**
 * Shadow-capable species in Pokémon GO (National Dex ids).
 *
 * PR-E10 fix 3: this roster is now auto-generated at build time from
 * https://pogoapi.net/api/v1/shadow_pokemon.json (the authoritative
 * community-maintained source), merged with a small manual additions
 * list in scripts/generate-shadow-roster.ts that covers shadows pogoapi
 * lags on. Refresh with `npm run generate:shadow`.
 *
 * Product intent (user direction, Push 6): the Shadow Dex tab must show the
 * available Shadow species as silhouettes on a fresh install instead of an
 * empty page. The user's caught/recorded state is overlaid on top, so an
 * entry stays a silhouette until they record that Shadow variant. Manual
 * edits and existing caught states are never overwritten — the grid unions
 * this roster with whatever the user already recorded.
 */
/**
 * Roster automation (2026-06): the bundled SHADOW_DEX_IDS_GENERATED is now only
 * the offline FLOOR. The authoritative, never-shrinking roster lives in the
 * Worker (D1 shadow_roster) and is fetched at runtime via useShadowRoster, which
 * calls setRuntimeShadowRoster() below. We UNION the fetched ids onto the
 * baseline so:
 *   - offline / pre-fetch: the app still has the full bundled floor, and
 *   - a transient short/empty fetch can never shrink the roster (we only add).
 * This is what stops newly-released Shadows (e.g. Ponyta/Krabby) from
 * false-blocking real catches at validateDexMember().
 */
/**
 * Curated Shadow-roster corrections (2026-07, device reconciliation).
 *
 * The bundled pogoapi-derived floor is wrong in BOTH directions for a handful of
 * species, so these lists override it. They apply to the Shadow AND Purified
 * dexes (which share this roster). Owner-verified in-game truth wins over the
 * feeds — the same pattern rosterCorrections.ts uses for the base/shiny rosters.
 *   - ADD: obtainable as Shadow but missing from the floor.
 *   - REMOVE: listed by the floor but NOT Shadow-obtainable. REMOVE also vetoes
 *     runtime (D1) additions below, so a stale worker roster can't re-add one.
 */
const SHADOW_ROSTER_ADD_IDS: readonly number[] = [
  86, 87, 163, 164, 610, 611, 612, 643, 714, 715, 821, 822, 823,
  // 2026-07-25 owner reconciliation: obtainable as Shadow/Purified in-game but
  // missing from the Game Master floor. #864 (Cursola) is the only one of the
  // batch the floor lacks; the rest (83/115/122/263/264/618/863/865/866/867/904)
  // are already in the floor and are re-enabled by clearing the REMOVE veto below.
  864,
];
// 2026-07-25: the previous curated removals (83/115/122/263/264/618/863/865/866/
// 867/904) are all confirmed Shadow/Purified-obtainable in-game, so the veto is
// now empty. Kept as an explicit empty set so a future audit can re-add a genuine
// non-Shadow id (and re-veto stale worker/feed additions) without reintroducing
// the wiring.
const SHADOW_ROSTER_REMOVE_IDS: ReadonlySet<number> = new Set<number>([]);

const runtimeShadowIdSet = new Set<number>(SHADOW_DEX_IDS_GENERATED);
SHADOW_ROSTER_ADD_IDS.forEach((id) => runtimeShadowIdSet.add(id));
SHADOW_ROSTER_REMOVE_IDS.forEach((id) => runtimeShadowIdSet.delete(id));

/**
 * The live Shadow roster as a STABLE, in-place-mutated array.
 *
 * It starts as the bundled baseline (the offline floor) and is only ever
 * extended in place by setRuntimeShadowRoster() with ids fetched from the
 * Worker. We mutate in place (never reassign) so the array's identity is stable:
 * existing imports of SHADOW_DEX_IDS, the CURATED_VARIANT_ROSTERS map, and
 * identity assertions all keep pointing at the same growing array. Kept strictly
 * ascending and de-duplicated to preserve the roster's ordering invariant.
 */
const SHADOW_DEX_IDS_MUTABLE: number[] = Array.from(runtimeShadowIdSet).sort((a, b) => a - b);

/**
 * Merge worker-fetched roster ids into the live array. Union-only: ids are
 * never removed, so the on-device roster can only ever grow beyond the bundled
 * baseline (never-shrink). Idempotent; re-sorts in place only when a genuinely
 * new id is added.
 */
export function setRuntimeShadowRoster(ids: readonly number[]): void {
  let grew = false;
  for (let i = 0; i < ids.length; i++) {
    const id = ids[i];
    // Skip curated removals so a stale/lagging worker roster can never re-add a
    // species we've vetoed as NOT Shadow-obtainable.
    if (
      Number.isFinite(id) &&
      id > 0 &&
      !SHADOW_ROSTER_REMOVE_IDS.has(id) &&
      !runtimeShadowIdSet.has(id)
    ) {
      runtimeShadowIdSet.add(id);
      SHADOW_DEX_IDS_MUTABLE.push(id);
      grew = true;
    }
  }
  if (grew) {
    SHADOW_DEX_IDS_MUTABLE.sort((a, b) => a - b);
  }
}

/**
 * Shadow-capable species: bundled baseline unioned with the worker roster.
 * Exposed as a readonly view of the stable live array so all consumers (and the
 * curated-roster map) see runtime additions without re-importing.
 */
export const SHADOW_DEX_IDS: readonly number[] = SHADOW_DEX_IDS_MUTABLE;

/**
 * Purified-capable species == Shadow-capable species (a Purified Pokémon is a
 * cleansed Shadow). Same stable array so both tabs stay in sync and both pick up
 * runtime additions.
 */
export const PURIFIED_DEX_IDS: readonly number[] = SHADOW_DEX_IDS;

/**
 * Variant → curated availability roster. A variant present here renders
 * its curated silhouette set even before the user has scanned anything.
 * Variants NOT present fall back to the full-National-Dex policy in
 * dexGridFilter (the user-attribute variants — pokemon/shiny/lucky/
 * perfect/xxs/xxl — are whole-species rosters with user state overlaid).
 *
 * mega/gmax/shadow/purified all use the same availability pathway: a
 * curated base-species roster, unioned with the user's recorded state in
 * dexGridFilter.baseDexNumbers. purified intentionally points at the same
 * array as shadow (purifiable == Shadow-available).
 */
/** Species that have at least one regional form / at least one GO costume. */
const REGIONAL_DEX_SPECIES_IDS: readonly number[] = formDexSpeciesIds("regional");
const COSTUME_DEX_SPECIES_IDS: readonly number[] = formDexSpeciesIds("costume");

const CURATED_VARIANT_ROSTERS: Partial<Record<PokedexVariant, readonly number[]>> = {
  mega: MEGA_DEX_IDS,
  gmax: GMAX_DEX_IDS,
  dynamax: DYNAMAX_DEX_IDS,
  shadow: SHADOW_DEX_IDS,
  purified: PURIFIED_DEX_IDS,
  // The form dexes' rosters are the SPECIES that have a regional form / a
  // costume; each of those species then renders one cell per form (see
  // formDexRoster). Derived from the generated forms registry, so a refresh that
  // adds a costume species adds it here too.
  regional: REGIONAL_DEX_SPECIES_IDS,
  costume: COSTUME_DEX_SPECIES_IDS,
};

/** The curated variants an admin roster override can target (mirrors the server
 *  ROSTER_OVERRIDE_VARIANTS). Used by the dex-config syncer to iterate. */
export const ROSTER_OVERRIDE_VARIANT_KEYS = [
  "mega", "gmax", "dynamax", "shadow", "purified",
] as const;
export type RosterOverrideVariantKey = (typeof ROSTER_OVERRIDE_VARIANT_KEYS)[number];

/** True when the variant has a curated GO-availability roster. */
export function hasCuratedRoster(variant: PokedexVariant): boolean {
  return variant in CURATED_VARIANT_ROSTERS;
}

/* ────────────────────────────────────────────────────────────────────────────
   Server-driven curated-roster overrides (admin dex-config editor).
   ----------------------------------------------------------------------------
   The bundled rosters above are the baseline. An admin can add/remove species
   from a curated dex (mega/gmax/dynamax) via the portal; the client fetches
   those overrides at launch (GET /api/dex-config) and applies them here so a new
   Mega (e.g. Mega Starmie) appears app-wide with no rebuild. Overrides are
   applied inside curatedRosterFor(), so every consumer — grid, availability,
   counts — picks them up. Distinct from the shadow runtime roster (union-only);
   these support add AND remove.
   ──────────────────────────────────────────────────────────────────────────── */
// Add overrides carry an optional release date (epoch-ms; null = immediate),
// evaluated at read time in curatedRosterFor so a scheduled Mega/G-Max release
// auto-appears once its date passes.
const runtimeRosterAdds: Partial<Record<PokedexVariant, Map<number, number | null>>> = {};
const runtimeRosterRemoves: Partial<Record<PokedexVariant, Set<number>>> = {};

/** A dex id with an optional release date, as delivered by the dex-config
 *  syncer. A plain number is accepted too (immediate add). */
type ScheduledAdd = number | { dexId: number; releaseAt: number | null };

/**
 * Replace the runtime add/remove overrides for one curated variant. Called by
 * the dex-config syncer with server data. `add` entries may be plain ids
 * (immediate) or `{ dexId, releaseAt }` (scheduled). Passing empty arrays clears
 * the variant's overrides (baseline shows through). No-op for variants without a
 * curated roster.
 */
export function setRuntimeCuratedRosterOverrides(
  variant: PokedexVariant,
  add: readonly ScheduledAdd[],
  remove: readonly number[],
): void {
  if (!(variant in CURATED_VARIANT_ROSTERS)) return;
  const addMap = new Map<number, number | null>();
  for (const a of add) {
    const id = typeof a === "number" ? a : a?.dexId;
    const at = typeof a === "number" ? null : typeof a?.releaseAt === "number" ? a.releaseAt : null;
    if (Number.isFinite(id) && id > 0) addMap.set(id, at);
  }
  runtimeRosterAdds[variant] = addMap;
  runtimeRosterRemoves[variant] = new Set(remove.filter((id) => Number.isFinite(id) && id > 0));
}

/**
 * The curated availability roster for a variant, or null when the variant
 * uses the full-National-Dex / recorded-only policy instead.
 *
 * Applies any admin roster overrides over the bundled baseline, honouring each
 * add's release date at READ time (a future-dated add is not yet a member). When
 * there are no overrides the baseline array is returned by identity (fast path +
 * keeps the shadow stable-identity invariant intact).
 */
export function curatedRosterFor(variant: PokedexVariant): readonly number[] | null {
  const base = CURATED_VARIANT_ROSTERS[variant];
  if (!base) return null;
  const add = runtimeRosterAdds[variant];
  const remove = runtimeRosterRemoves[variant];
  if ((!add || add.size === 0) && (!remove || remove.size === 0)) return base;
  const now = Date.now();
  const set = new Set<number>(base);
  add?.forEach((at, id) => {
    if (at == null || at <= now) set.add(id);
  });
  remove?.forEach((id) => set.delete(id));
  return Array.from(set).sort((a, b) => a - b);
}

/**
 * The curated roster narrowed to what Pokémon GO has RELEASED, for the
 * availability layer (variantAvailability.availableRosterForVariant). Every
 * curated dex but G-Max lists released members only, so for them this is
 * curatedRosterFor by identity (the shadow stable-identity invariant holds).
 * The G-Max dex lists every Gigantamax form that exists (GMAX_DEX_IDS), so its
 * released set is the members in GMAX_RELEASED_IDS plus any portal add whose
 * date has passed: an admin add IS a release, and a dated add on a form the dex
 * already lists (Melmetal scheduled for its Max Battle Day) keeps that cell
 * "unreleased" until the day, flipping at read time like every other schedule.
 */
export function curatedReleasedRosterFor(variant: PokedexVariant): readonly number[] | null {
  const members = curatedRosterFor(variant);
  if (!members || variant !== "gmax") return members;
  const adds = runtimeRosterAdds.gmax;
  const now = Date.now();
  const addedByNow = (id: number): boolean => {
    if (!adds?.has(id)) return false;
    const at = adds.get(id);
    return at == null || at <= now;
  };
  return members.filter((id) => GMAX_RELEASED_SET.has(id) || addedByNow(id));
}

/** True when id is a member of the Hisui evolution-line dex grouping. */
export function isHisuiDexId(id: number): boolean {
  return HISUI_DEX_IDS.includes(id);
}

/**
 * Run-3 false-write hardening (telemetry 2026-06-21).
 *
 * The live write boundary already validates that an OCR'd dex id lands inside
 * the active REGION range (pogoSlotMapping.resolveCellNationalDex) and crosses
 * it against the on-screen region (dexCrossCheck). What it did NOT validate is
 * whether `(variant, dexId)` is a real member of that variant's curated roster.
 *
 * That is the exact gap the device run exposed on the G-Max Kanto page: OCR read
 * `U145` → #145 (Articuno) and it was applied under `variant:"gmax"` even though
 * Articuno has no Gigantamax form. #145 IS a valid Kanto member (1..151) and IS
 * region-consistent, so every existing gate passed it — only a roster-membership
 * check rejects it. Likewise #25 (Pikachu) under gmax, #122/#569 OCR garbage.
 *
 * Rule: a variant that has a curated roster (mega/gmax/shadow/purified — the
 * variants whose dex tab is a fixed, knowable subset) may only write a dex id
 * that is in that roster. Variants WITHOUT a curated roster (pokemon/shiny/
 * lucky/perfect/xxs/xxl — whole-species attribute dexes that legitimately span
 * the full National Dex) are not roster-restricted; they are validated by the
 * region/range checks elsewhere only.
 *
 * Returns `{ valid, reason }`. `reason` is a short, greppable marker for the
 * device telemetry ("not-in-curated-roster" / "ok").
 */
export function validateDexMember(
  variant: PokedexVariant,
  dexId: number,
): { valid: boolean; reason: "ok" | "not-in-curated-roster" } {
  const roster = curatedRosterFor(variant);
  if (roster == null) return { valid: true, reason: "ok" };
  return roster.includes(dexId)
    ? { valid: true, reason: "ok" }
    : { valid: false, reason: "not-in-curated-roster" };
}
